import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button,
  Slider, Typography, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { color, font } from '../styles/tokens';
import { getCroppedImageFile } from '../utils/cropImage';

const RATIOS = [
  { key: 'original', label: 'Original', value: null },
  { key: '1:1', label: '1:1', value: 1 },
  { key: '4:3', label: '4:3', value: 4 / 3 },
  { key: '3:2', label: '3:2', value: 3 / 2 },
  { key: '16:9', label: '16:9', value: 16 / 9 },
];

// `source` is either a File (freshly picked) or a plain
// { url, name, type } reference to an already-uploaded remote image.
function ImageCropDialog({ open, source, onCancel, onApply, onError }) {
  const isRemote = source && !(source instanceof File);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [ratioKey, setRatioKey] = useState('original');
  const [naturalAspect, setNaturalAspect] = useState(4 / 3);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!source) { setImageSrc(null); return; }
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRatioKey('original');
    if (isRemote) {
      setImageSrc(source.url);
      return;
    }
    const url = URL.createObjectURL(source);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const aspect = useMemo(() => {
    const preset = RATIOS.find(r => r.key === ratioKey);
    return preset && preset.value ? preset.value : naturalAspect;
  }, [ratioKey, naturalAspect]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const onMediaLoaded = useCallback(({ width, height }) => {
    if (width && height) setNaturalAspect(width / height);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels || !source) return;
    setSaving(true);
    const fileName = isRemote ? (source.name || 'image.jpg') : source.name;
    const mimeType = isRemote ? (source.type || 'image/jpeg') : source.type;
    try {
      const cropped = await getCroppedImageFile(imageSrc, croppedAreaPixels, fileName, mimeType);
      onApply(cropped);
    } catch (err) {
      if (isRemote) {
        onError?.('Couldn’t crop this image — it’s hosted somewhere that doesn’t allow in-browser editing. Upload a replacement instead.');
      } else {
        onError?.('Couldn’t crop this image. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: font.display, fontWeight: 600 }}>Crop attached art</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontFamily: font.mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase', color: color.boneFaint, mb: 1 }}>
          Aspect ratio
        </Typography>
        <ToggleButtonGroup
          value={ratioKey}
          exclusive
          onChange={(_, v) => v && setRatioKey(v)}
          size="small"
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          {RATIOS.map(r => (
            <ToggleButton key={r.key} value={r.key} sx={{ fontFamily: font.mono, fontSize: '.75rem' }}>
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {imageSrc && (
          <Box sx={{ position: 'relative', width: '100%', height: 320, background: '#000', border: `1px solid ${color.ruleSoft}` }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              onMediaLoaded={onMediaLoaded}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Typography sx={{ fontFamily: font.mono, fontSize: '.75rem', color: color.boneFaint }}>Zoom</Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.01}
            onChange={(_, v) => setZoom(v)}
            sx={{ color: color.cyan }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">Cancel</Button>
        <Button onClick={handleApply} variant="contained" color="primary" disabled={saving || !croppedAreaPixels}>
          {saving ? 'Applying…' : 'Apply crop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImageCropDialog;
